import "./roomitemlist.css";
import { useEffect, useMemo, useState } from "react";
import API from "src/API2";
import toast from "react-hot-toast";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcSearch = () => <Svg><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></Svg>;
const IcClose  = () => <Svg size={18}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcTrash  = () => <Svg size={14}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></Svg>;
const IcChevL  = () => <Svg size={14}><polyline points="15 18 9 12 15 6" /></Svg>;
const IcChevR  = () => <Svg size={14}><polyline points="9 18 15 12 9 6" /></Svg>;
const IcEmpty  = () => <Svg size={32}><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" /></Svg>;

const RoomItemList = () => {
  const [rooms, setRooms] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ floor: "", hotelRoom: "", available: "" });

  const roomsPerPage = 10;

  useEffect(() => {
    setLoading(true);
    API.get("roomitems")
      .then((resp) => {
        if (resp.data && Array.isArray(resp.data.rooms)) {
          setRooms(resp.data.rooms.sort((a, b) => a.number - b.number));
        } else {
          setRooms([]);
          setError(resp.data?.message || "No rooms returned");
        }
      })
      .catch((e) => {
        console.error(e);
        setError("Error fetching room items.");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const filtered = useMemo(() => {
    let out = [...rooms];
    if (filters.floor) {
      out = out.filter((r) => r.floor && r.floor.level?.toString() === filters.floor);
    }
    if (filters.hotelRoom) {
      out = out.filter((r) => r.hotelRoom && r.hotelRoom.code === filters.hotelRoom);
    }
    if (filters.available) {
      const av = filters.available === "true";
      out = out.filter((r) => r.available === av);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      out = out.filter(
        (r) =>
          String(r.number ?? "").toLowerCase().includes(q) ||
          (r.hotelRoom?.code || "").toLowerCase().includes(q) ||
          String(r.floor?.level ?? "").toLowerCase().includes(q),
      );
    }
    return out;
  }, [rooms, filters, searchQuery]);

  /* Reset page when filters/search change */
  useEffect(() => { setCurrentPage(1); }, [filters, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / roomsPerPage));
  const indexOfLast = currentPage * roomsPerPage;
  const indexOfFirst = indexOfLast - roomsPerPage;
  const currentRooms = filtered.slice(indexOfFirst, indexOfLast);

  const counts = useMemo(() => {
    const total = rooms.length;
    const available = rooms.filter((r) => r.available).length;
    const occupied = total - available;
    const floors = new Set(rooms.map((r) => r.floor?.level).filter((v) => v !== undefined)).size;
    return { total, available, occupied, floors };
  }, [rooms]);

  const floorOptions = useMemo(
    () =>
      [...new Set(rooms.map((r) => r.floor?.level))]
        .filter((v) => v !== undefined)
        .sort((a, b) => Number(a) - Number(b)),
    [rooms],
  );
  const codeOptions = useMemo(
    () => [...new Set(rooms.map((r) => r.hotelRoom?.code))].filter(Boolean),
    [rooms],
  );

  const handleDelete = (room) => {
    if (!window.confirm(`Delete room ${room.number}? This cannot be undone.`)) return;
    toast.promise(
      API.delete(`roomitems/${room.id}`).then(() => {
        setRooms((prev) => prev.filter((r) => r.id !== room.id));
      }),
      {
        success: `Room ${room.number} deleted`,
        error: "Could not delete this room",
        loading: "Deleting room…",
      },
    );
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
  };

  if (error) {
    return (
      <div className="ri-empty">
        <IcEmpty />
        <h3>Couldn't load rooms</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ri-card">
        <div className="ri-table-wrap">
          <table className="ri-table">
            <thead>
              <tr>
                <th>#</th><th>Number</th><th>Floor</th><th>Room type</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan="6"><div className="ri-skel" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!rooms.length) {
    return (
      <div className="ri-empty">
        <IcEmpty />
        <h3>No rooms yet</h3>
        <p>Add rooms to start receiving reservations.</p>
      </div>
    );
  }

  return (
    <div className="ri-list">
      {/* Mini stats */}
      <div className="ri-stats">
        <div className="ri-stat">
          <span className="ri-stat__label">Total rooms</span>
          <span className="ri-stat__value">{counts.total}</span>
        </div>
        <div className="ri-stat">
          <span className="ri-stat__label">Available</span>
          <span className="ri-stat__value ri-stat__value--green">{counts.available}</span>
        </div>
        <div className="ri-stat">
          <span className="ri-stat__label">Off-market</span>
          <span className="ri-stat__value ri-stat__value--muted">{counts.occupied}</span>
        </div>
        <div className="ri-stat">
          <span className="ri-stat__label">Floors</span>
          <span className="ri-stat__value">{counts.floors}</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="ri-toolbar">
        <div className="ri-search">
          <IcSearch />
          <input
            type="text"
            placeholder="Search by number, type code, or floor…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="ri-search__clear" onClick={() => setSearchQuery("")} aria-label="Clear">
              <IcClose />
            </button>
          )}
        </div>
        <div className="ri-filters">
          <label className="ri-select">
            <span>Floor</span>
            <select name="floor" value={filters.floor} onChange={handleFilterChange}>
              <option value="">All</option>
              {floorOptions.map((lvl, i) => (
                <option key={i} value={lvl}>{lvl}</option>
              ))}
            </select>
          </label>
          <label className="ri-select">
            <span>Type</span>
            <select name="hotelRoom" value={filters.hotelRoom} onChange={handleFilterChange}>
              <option value="">All</option>
              {codeOptions.map((c, i) => (
                <option key={i} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="ri-select">
            <span>Status</span>
            <select name="available" value={filters.available} onChange={handleFilterChange}>
              <option value="">All</option>
              <option value="true">Available</option>
              <option value="false">Off market</option>
            </select>
          </label>
        </div>
      </div>

      {/* Card + table */}
      <div className="ri-card">
        <div className="ri-table-wrap">
          <table className="ri-table">
            <thead>
              <tr>
                <th className="ri-no">#</th>
                <th>Room number</th>
                <th>Floor</th>
                <th>Room type</th>
                <th>Status</th>
                <th className="ri-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRooms.length === 0 ? (
                <tr>
                  <td colSpan="6">
                    <div className="ri-empty ri-empty--inline">
                      <IcEmpty />
                      <span>No rooms match your filters.</span>
                    </div>
                  </td>
                </tr>
              ) : (
                currentRooms.map((room, idx) => (
                  <tr key={room.id} className="ri-row">
                    <td className="ri-no">{indexOfFirst + idx + 1}</td>
                    <td>
                      <div className="ri-room-num">
                        <span className="ri-room-num__label">Room</span>
                        <strong>{room.number}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="ri-floor-chip">Floor {room.floor?.level ?? "—"}</span>
                    </td>
                    <td>
                      <span className="ri-type-chip">{room.hotelRoom?.code || "—"}</span>
                    </td>
                    <td>
                      <span className={`ri-status ri-status--${room.available ? "on" : "off"}`}>
                        {room.available ? "Available" : "Off market"}
                      </span>
                    </td>
                    <td className="ri-actions">
                      <button
                        className="ri-icon-btn ri-icon-btn--danger"
                        onClick={() => handleDelete(room)}
                        title="Delete room"
                      >
                        <IcTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="ri-pagination">
            <span className="ri-pagination__hint">
              Showing {indexOfFirst + 1}–{Math.min(indexOfLast, filtered.length)} of {filtered.length}
            </span>
            <div className="ri-pagination__controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="ri-icon-btn"
              >
                <IcChevL />
              </button>
              <span className="ri-pagination__page">Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ri-icon-btn"
              >
                <IcChevR />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomItemList;
